// Shared media atoms for the competitions components: crest, player photo
// and nationality flag — all with the platform's fallback rules ("empty
// renders nothing", monogram/initials instead of broken images, never a
// bare emoji flag on Windows — StandingsWidget.js:63 pattern).
import React, { useState } from "react";
import countryToFlag, { toIso } from "../../../utils/countryFlag";
import { C, initialsOf } from "./competitionsUi";

const IS_WINDOWS = (() => {
  if (typeof navigator === "undefined") return false;
  const ua = navigator.userAgent || "";
  const platform = navigator.platform || "";
  return /Win/i.test(platform) || /Windows/i.test(ua);
})();

// Crest: image with monogram fallback (UI-SPEC §1.7 — box #F1F5FA,
// 1px #E2E8F0 border, bold #475569 initials, radius 2px).
export const CompetitionCrest = ({ name, logo, size = 24 }) => {
  const [broken, setBroken] = useState(false);
  const showImage = logo && !broken;
  return (
    <span
      className="notranslate"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: 2,
        background: C.placeholderBg,
        border: `1px solid ${C.border}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        fontSize: Math.max(7, Math.round(size * 0.34)),
        fontWeight: 700,
        color: C.ink600,
      }}
    >
      {showImage ? (
        <img
          src={logo}
          alt=""
          onError={() => setBroken(true)}
          style={{ width: "100%", height: "100%", objectFit: "contain" }}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
};

// Player photo: circle with initials fallback (UI-SPEC §1.6 border-radius 50%).
export const PlayerPhoto = ({ name, photo, size = 26 }) => {
  const [broken, setBroken] = useState(false);
  const showImage = photo && !broken;
  return (
    <span
      className="notranslate"
      aria-hidden="true"
      style={{
        width: size,
        height: size,
        flex: "none",
        borderRadius: "50%",
        background: C.placeholderBg,
        border: `1px solid ${C.border}`,
        display: "inline-flex",
        alignItems: "center",
        justifyContent: "center",
        overflow: "hidden",
        fontSize: Math.max(8, Math.round(size * 0.36)),
        fontWeight: 700,
        color: C.ink600,
      }}
    >
      {showImage ? (
        <img
          src={photo}
          alt=""
          onError={() => setBroken(true)}
          style={{ width: "100%", height: "100%", objectFit: "cover" }}
        />
      ) : (
        initialsOf(name)
      )}
    </span>
  );
};

// Nationality flag. Accepts a name ("Spain"), ISO code ("ES") or the small
// {name, code} object countryFlag understands. Unresolvable -> renders
// nothing (never a placeholder). On Windows the emoji doesn't render as a
// flag, so a small letter chip is shown instead (StandingsWidget pattern).
export const CompetitionFlag = ({ country, size = 12 }) => {
  if (!country) return null;
  const emoji = countryToFlag(country);
  const iso = toIso(country);
  if (!emoji && !iso) return null;
  if (IS_WINDOWS || !emoji) {
    if (!iso) return null;
    return (
      <span
        className="notranslate"
        aria-hidden="true"
        style={{
          display: "inline-flex",
          alignItems: "center",
          justifyContent: "center",
          flex: "none",
          minWidth: size + 4,
          height: size,
          padding: "0 2px",
          borderRadius: 2,
          background: C.placeholderBg,
          border: `1px solid ${C.border}`,
          fontSize: Math.max(7, Math.round(size * 0.62)),
          fontWeight: 700,
          letterSpacing: ".04em",
          color: C.ink600,
        }}
      >
        {iso}
      </span>
    );
  }
  return (
    <span
      className="notranslate"
      aria-hidden="true"
      style={{ fontSize: size, lineHeight: 1, flex: "none" }}
    >
      {emoji}
    </span>
  );
};
